'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LogIn, LogOut, UserPlus, UserCircle } from 'lucide-react'; // Icons

import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/auth-context';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton'; // Import Skeleton

// Auth links for logged-out users (moved from AppSidebar)
const authMenuItems = [
  {
    href: '/login',
    label: 'Login',
    icon: LogIn,
  },
  {
    href: '/signup',
    label: 'Sign Up',
    icon: UserPlus,
  },
];

export function AuthSection() {
  const pathname = usePathname();
  const { user, userProfile, loading, logout } = useAuth();
  const showLoggedInStructure = loading || (user && userProfile);

  return (
    <>
      {showLoggedInStructure ? (
        <>
          {/* User Info Section - Directly in fragment */}
          <div className='flex items-center gap-2 mb-2 group-data-[collapsible=icon]:justify-center px-1'>
            <Avatar className='h-8 w-8 group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6'>
              {loading ? (
                <Skeleton className='h-full w-full rounded-full' />
              ) : (
                <>
                  <AvatarImage
                    src={user?.photoURL || undefined}
                    alt={userProfile?.displayName || user?.email || 'User'}
                  />
                  <AvatarFallback>
                    <UserCircle />
                  </AvatarFallback>
                </>
              )}
            </Avatar>
            <div className='text-sm overflow-hidden group-data-[collapsible=icon]:hidden'>
              {loading ? (
                <div className='space-y-1'>
                  <Skeleton className='h-4 w-24' />
                  <Skeleton className='h-3 w-16' />
                </div>
              ) : (
                <>
                  <p className='font-medium truncate'>
                    {userProfile?.displayName || user?.email}
                  </p>
                  <p className='text-xs text-muted-foreground capitalize'>
                    {userProfile?.role || 'User'}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Logout Button within Menu structure */}
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                size='sm'
                onClick={logout}
                className='w-full justify-start gap-2'
                disabled={loading}
              >
                <LogOut />
                <span className='group-data-[collapsible=icon]:hidden'>
                  Logout
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </>
      ) : (
        // Logged Out View
        <SidebarMenu>
          {authMenuItems.map((item) => (
            <SidebarMenuItem key={item.label}>
              <SidebarMenuButton
                asChild
                className={cn(
                  'justify-start gap-2',
                  pathname === item.href && 'bg-muted font-semibold'
                )}
              >
                <Link href={item.href}>
                  <item.icon />
                  <span>{item.label}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      )}
    </>
  );
}
